import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useHasPermission } from "../../../access/use-access"
import { fetchCatalog, fetchEmployees, fetchPositions, updateEmployeeProfile } from "../../../api/directory.api"
import { isApiProblem } from "../../../api/problem"
import { ProblemScreen } from "../../../shell/states"
import { Badge, Button, SelectField, Skeleton, Switch, TextAreaField, TextField } from "../../../ui"
import { CenterState, StandHeader } from "../stand"
import { useResource } from "../use-resource"
import { isConnectionProblem } from "./member-model"
import {
  type ProfileForm,
  emptyProfileHint,
  formOf,
  previewServices,
  profileErrors,
  publicationOf,
} from "./profile-model"
import styles from "./employee-profile-screen.module.css"

/**
 * 3-Б7 «Публичный профиль мастера» (макет П2, ТЗ 03, Б7).
 *
 * Слева — четыре поля, которые уходят на витрину, и выключатель показа; справа —
 * карточка в том виде, в каком её увидит клиент. Рядом — что на витрину НЕ уходит:
 * владельцы боятся, что «клиент увидит зарплату», и ответ должен стоять на экране.
 *
 * Своя страница (/staff/:id/profile), а не окно: на телефоне предпросмотр — отдельным
 * экраном по кнопке «Как видит клиент» (ТЗ Б7, узкий экран).
 *
 * ФОТО ЖДЁТ ХРАНИЛИЩА ФАЙЛОВ. Кнопка стоит, где в макете, но закрыта с причиной:
 * загрузка — отдельный путь с хранилищем, которое ещё не подключено (решение владельца
 * 11.10.2026: доступы к S3 будут позже).
 */
export function EmployeeProfileScreen(props: {
  readonly employeeId: string
  readonly onClose: () => void
}): ReactNode {
  const canManage = useHasPermission("staff.manage")
  const employees = useResource((signal) => fetchEmployees(false, signal), "profile-employees")
  const positions = useResource((signal) => fetchPositions(signal), "profile-positions")
  const catalog = useResource((signal) => fetchCatalog(signal), "profile-catalog")

  const employee = employees.data?.find((item) => item.id === props.employeeId) ?? null
  const [form, setForm] = useState<ProfileForm | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<{ readonly tone: "ok" | "error"; readonly text: string } | null>(null)
  const [previewOnly, setPreviewOnly] = useState(false)

  // Форма заполняется один раз, когда карточка приехала; перезагрузка списка после
  // сохранения не должна затирать то, что человек уже набирает дальше.
  useEffect(() => {
    if (employee !== null && form === null) setForm(formOf(employee))
  }, [employee, form])

  if (employees.problem !== null && employees.data === null) {
    return <ProblemScreen problem={employees.problem} onRetry={employees.reload} />
  }
  if (employees.data !== null && employee === null) {
    return (
      <div className={styles.screen}>
        <StandHeader title="Профиль мастера" />
        <CenterState
          title="Мастер не найден"
          text="Возможно, его перевели в архив. Вернитесь к списку сотрудников."
          actions={<Button onClick={props.onClose}>К сотрудникам</Button>}
        />
      </div>
    )
  }
  if (employee === null || form === null) {
    return (
      <div className={styles.screen}>
        <StandHeader title="Профиль мастера" />
        <div className={styles.card}>
          <Skeleton width="100%" height={320} radius={12} />
        </div>
      </div>
    )
  }

  const errors = showErrors ? profileErrors(form) : null
  const update = (patch: Partial<ProfileForm>): void => {
    setForm({ ...form, ...patch })
    setOutcome(null)
  }
  const denied = canManage ? undefined : "Описание на сайте редактирует управляющий"
  const publication = publicationOf({
    ...employee,
    publicName: form.publicName,
    isOnlineBookable: form.isOnlineBookable,
  })
  const services = catalog.data === null ? null : previewServices(employee, catalog.data.services)
  const positionName =
    positions.data?.find((position) => position.id === form.positionId)?.name ?? null
  const hint = emptyProfileHint(employee, form)

  const save = async (): Promise<void> => {
    setShowErrors(true)
    if (profileErrors(form) !== null) return
    setBusy(true)
    setOutcome(null)
    try {
      await updateEmployeeProfile({
        employeeId: employee.id,
        publicName: form.publicName.trim(),
        positionId: form.positionId === "" ? null : form.positionId,
        about: form.about.trim() === "" ? null : form.about.trim(),
        isOnlineBookable: form.isOnlineBookable,
        expectedUpdatedAt: employee.updatedAt ?? new Date(0).toISOString(),
      })
      setOutcome({ tone: "ok", text: "Профиль сохранён — клиенты видят его на странице записи." })
      employees.reload()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setOutcome({
        tone: "error",
        text: isConnectionProblem(error)
          ? "Не сохранилось: пропала связь. На витрине прежний профиль. Повторите."
          : error.code === "version_conflict"
            ? "Карточку мастера только что изменил кто-то ещё. Обновите страницу — набранное придётся повторить."
            : (error.detail ?? error.title),
      })
    } finally {
      setBusy(false)
    }
  }

  const preview = (
    <div className={styles.previewColumn}>
      <span className={styles.hint}>Предпросмотр на витрине</span>
      <div className={styles.previewCard}>
        {employee.avatarUrl === null ? (
          <div className={styles.previewPhoto} aria-hidden="true" />
        ) : (
          <img className={styles.previewPhoto} src={employee.avatarUrl} alt="" />
        )}
        <div className={styles.previewName}>{form.publicName.trim() === "" ? "Имя для клиентов" : form.publicName.trim()}</div>
        {positionName === null ? null : <div className={styles.previewPosition}>{positionName}</div>}
        {form.about.trim() === "" ? null : <div className={styles.previewAbout}>{form.about.trim()}</div>}
        {services === null || (services.names.length === 0 && services.more === 0) ? null : (
          <div className={styles.previewServices}>
            {services.names.map((name) => (
              <Badge key={name} tone="gray">
                {name}
              </Badge>
            ))}
            {services.more === 0 ? null : <Badge tone="gray">+ {services.more}</Badge>}
          </div>
        )}
        {/* Кнопка карточки — картинка, а не действие: здесь она никуда не ведёт. */}
        <span className={styles.previewButton} aria-hidden="true">
          Записаться
        </span>
      </div>
      {hint === null ? null : <div className={styles.hint}>{hint}</div>}
    </div>
  )

  if (previewOnly) {
    return (
      <div className={styles.screen}>
        <StandHeader title={`${employee.displayName} · как видит клиент`} />
        <div className={styles.card}>
          {preview}
          <Button onClick={() => setPreviewOnly(false)}>К профилю</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.screen}>
      <StandHeader title="Профиль мастера">
        <Button size="sm" onClick={props.onClose}>
          К сотрудникам
        </Button>
      </StandHeader>

      <div className={styles.card}>
        <div className={styles.formColumn}>
          <div>
            <div className={styles.title}>{employee.displayName} · как видит клиент</div>
            <div className={styles.subtitle}>Ровно четыре поля уходят на витрину</div>
          </div>

          <PublicationLine kind={publication.kind} displayName={employee.displayName} />

          <div className={styles.photoRow}>
            {employee.avatarUrl === null ? (
              <div className={styles.photo} aria-hidden="true">
                фото
              </div>
            ) : (
              <img className={styles.photo} src={employee.avatarUrl} alt="" />
            )}
            <div className={styles.photoActions}>
              <Button size="sm" deniedReason="Загрузка фото заработает, когда салону подключат хранилище файлов">
                Загрузить фото
              </Button>
              <span className={styles.hint}>JPG, PNG или HEIC · сжимаем сами</span>
            </div>
          </div>

          <TextField
            label="Публичное имя"
            required
            value={form.publicName}
            maxLength={80}
            disabled={!canManage}
            hint="«Аня», а не «Соколова А. С.» — клиенту не нужна фамилия, салону в журнале нужна"
            error={errors?.publicName ?? undefined}
            onChange={(event) => update({ publicName: event.target.value })}
          />

          <SelectField
            label="Должность"
            value={form.positionId}
            disabled={!canManage}
            options={[
              { value: "", label: positions.loading ? "Загружаем должности…" : "Без должности" },
              ...(positions.data ?? [])
                .filter((position) => position.isActive || position.id === form.positionId)
                .map((position) => ({ value: position.id, label: position.name })),
            ]}
            onChange={(event) => update({ positionId: event.target.value })}
          />

          <TextAreaField
            label="Описание"
            value={form.about}
            disabled={!canManage}
            error={errors?.about ?? undefined}
            onChange={(event) => update({ about: event.target.value })}
          />

          <Switch
            className={styles.switchRow}
            checked={form.isOnlineBookable}
            disabled={!canManage || !employee.isBookable}
            onChange={(checked) => update({ isOnlineBookable: checked })}
          >
            Показывать на странице записи
          </Switch>

          <div className={styles.notShared}>
            <b>Не уходит на витрину:</b> телефон, учётная запись, дата найма, цвет в журнале, персональные цены,
            отметка тарификации. Персональная цена видна клиенту только в цене выбранного слота.
          </div>

          {outcome === null ? null : (
            <div className={outcome.tone === "ok" ? styles.outcome : styles.problem} role={outcome.tone === "ok" ? "status" : "alert"}>
              {outcome.text}
            </div>
          )}

          <div className={styles.buttons}>
            <Button variant="primary" deniedReason={denied} loading={busy} onClick={() => void save()}>
              Сохранить
            </Button>
            <span className={styles.previewToggle}>
              <Button onClick={() => setPreviewOnly(true)}>Как видит клиент</Button>
            </span>
          </div>
        </div>

        <div className={styles.previewDesktop}>{preview}</div>
      </div>
    </div>
  )
}

/**
 * Где мастер сейчас. «Нет имени» — главное: без него мастера нет на витрине, а сообщения
 * о записях, сделанных администратором, назовут его учётным именем (решение П2-00).
 */
function PublicationLine(props: { readonly kind: ReturnType<typeof publicationOf>["kind"]; readonly displayName: string }): ReactNode {
  switch (props.kind) {
    case "published":
      return <div className={styles.status}><Badge tone="ok">на витрине</Badge></div>
    case "hidden":
      return (
        <div className={styles.status}>
          <Badge tone="warn">скрыт с витрины</Badge> Записать к мастеру можно только через администратора.
        </div>
      )
    case "no-name":
      return (
        <div className={styles.status}>
          <Badge tone="warn">нет имени для клиентов</Badge> Пока имени нет, мастера не видно на странице записи, а в
          сообщениях о записях из кабинета клиент увидит «{props.displayName}».
        </div>
      )
    default:
      return (
        <div className={styles.status}>
          <Badge tone="gray">не исполнитель</Badge> К этому сотруднику не записывают — на витрину он не выходит.
        </div>
      )
  }
}
